<template>
  <div>
    <div class="card">
      <div class="card-header d-flex justify-content-between">
        <div>
          <i class="fa fa-star mr-05" /> Notes et commentaires des clients
        </div>
        <div class="d-flex flex-column align-items-end" style="gap: 0.5rem">
          <div v-if="!loading">Total ({{ ratings.length }})</div>
          <button
            v-if="!loading && canEdit && clientOptions.length > 0"
            class="btn-sm btn btn-primary"
            @click="ratingModal.show = true"
          >
            <i class="fa fa-plus mr-05"></i> Ajouter
          </button>
        </div>
      </div>
      <div class="card-block">
        <div class="alert alert-info text-center" v-if="loading">
          Chargement...
        </div>

        <template v-else>
          <el-table
            :data="paginatedRatings"
            max-height="600"
            :default-sort="{ prop: 'createdAt', order: 'descending' }"
            @sort-change="handleSortChange"
          >
            <el-table-column
              label="Date"
              width="100"
              prop="createdAt"
              sortable="custom"
            >
              <template slot-scope="scope">
                <span class="small">{{ formatDate(scope.row.createdAt) }}</span>
              </template>
            </el-table-column>

            <el-table-column label="Client" width="200" prop="clientEmail">
              <template slot-scope="scope">
                <a v-if="scope.row.clientHref" :href="scope.row.clientHref"
                  ><i class="icon-user mr-05"></i
                  >{{ scope.row.clientName || scope.row.clientEmail }}</a
                >
                <span v-else>{{
                  scope.row.clientName || scope.row.clientEmail
                }}</span>
              </template>
            </el-table-column>

            <el-table-column
              label="Note"
              width="100"
              prop="rating"
              sortable="custom"
            >
              <template slot-scope="scope">
                <span class="text-primary">
                  <i
                    v-for="star in parseInt(scope.row.rating)"
                    :key="star"
                    class="fa fa-star"
                  />
                </span>
              </template>
            </el-table-column>

            <el-table-column label="Commentaire" min-width="300" prop="comment">
              <template slot-scope="scope">
                <p v-if="scope.row.comment" class="font-italic mt-1">
                  <i class="fa fa-quote-left text-sm" />
                  {{ scope.row.comment }}
                  <i class="fa fa-quote-right text-sm" />
                </p>
              </template>
            </el-table-column>

            <el-table-column v-if="canEdit" width="80" align="center">
              <template slot-scope="scope">
                <button
                  class="btn btn-sm btn-outline-info"
                  @click="editRating(scope.row)"
                >
                  <i class="fa fa-edit"></i>
                </button>
              </template>
            </el-table-column>
          </el-table>

          <paginate
            v-if="ratings.length > 0 && pageCount > 1"
            :key="sort.prop + sort.order"
            :page-count="pageCount"
            :click-handler="paginationHandler"
            prev-text="Précédent"
            next-text="Suivant"
            container-class="pagination"
            class="m-1"
          ></paginate>
        </template>
      </div>
    </div>

    <modal
      v-if="!loading && canEdit"
      :title="editingRating ? 'Modifier l\'avis' : 'Ajouter un avis'"
      v-model="ratingModal.show"
      @ok="editingRating ? handleEditRating() : handleAddRating()"
      @cancel="closeEditModal"
      effect="fade/zoom"
    >
      <div class="form-group">
        <label>Client</label>
        <v-select
          v-model="ratingModal.rating.clientEmail"
          :options="clientOptions"
          :reduce="({ value }) => value"
          :disabled="editingRating"
        ></v-select>
      </div>

      <div class="form-group">
        <label>Note</label>
        <v-select
          v-model="ratingModal.rating.rating"
          :options="ratingOptions"
        ></v-select>

        <div v-if="ratingModal.rating.rating" class="text-primary mt-1">
          <i
            v-for="star in parseInt(ratingModal.rating.rating)"
            :key="star"
            class="fa fa-star"
          />
        </div>
      </div>

      <div class="form-group">
        <label>Commentaire</label>
        <el-input
          v-model="ratingModal.rating.comment"
          type="textarea"
          :rows="4"
        ></el-input>
      </div>

      <div v-if="ratingModal.error" class="alert alert-danger">
        {{ ratingModal.error }}
      </div>

      <div slot="modal-footer" class="modal-footer">
        <button type="button" class="btn btn-default" @click="closeEditModal">
          Annuler
        </button>

        <button
          type="button"
          class="btn btn-primary"
          @click="editingRating ? handleEditRating() : handleAddRating()"
          :disabled="ratingModal.loading"
        >
          {{ editingRating ? "Mettre à jour" : "Ajouter" }}
        </button>
      </div>
    </modal>
  </div>
</template>

<script>
import Paginate from "vuejs-paginate";
import Modal from "vue-strap/src/Modal";

export default {
  components: {
    Paginate,
    Modal,
  },

  props: {
    coach: {
      type: Object,
      required: true,
    },

    clients: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      loading: false,
      ratings: [],
      currentPage: 1,
      limit: 5,
      sort: { prop: "createdAt", order: "descending" },
      ratingOptions: [1, 2, 3, 4, 5],
      ratingModal: {
        show: false,
        loading: false,
        error: null,
        rating: {
          id: null,
          clientEmail: "",
          rating: null,
          comment: "",
        },
      },
    };
  },

  async created() {
    await this.fetchClientsRatings();
  },

  computed: {
    mappedRatings() {
      return this.ratings.map((rating) => {
        const client = this.clients.find((c) => c.email === rating.clientEmail);

        if (client) {
          const route = this.$router.resolve({
            name: "client-details",
            params: { id: client.id },
          });

          return {
            ...rating,
            clientName: client.fullName,
            clientHref: route.href,
          };
        }

        return {
          ...rating,
          clientName: null,
          clientHref: null,
        };
      });
    },

    sortedRatings() {
      if (!this.sort.prop || !this.sort.order) {
        return this.mappedRatings;
      }

      const dir = this.sort.order === "ascending" ? 1 : -1;

      return [...this.mappedRatings].sort((a, b) => {
        switch (this.sort.prop) {
          case "createdAt":
            return dir * (new Date(a.createdAt) - new Date(b.createdAt));
          case "rating":
            return dir * (a.rating - b.rating);
          default:
            return 0;
        }
      });
    },

    paginatedRatings() {
      const start = (this.currentPage - 1) * this.limit;
      const end = start + this.limit;

      return this.sortedRatings.slice(start, end);
    },

    pageCount() {
      return Math.ceil(this.ratings.length / this.limit);
    },

    canEdit() {
      return this.hasPermission(
        this.$store.state.user,
        "CLIENTS_USERS_RATINGS_WRITE",
      );
    },

    editingRating() {
      return !!this.ratingModal.rating?.id;
    },

    clientOptions() {
      return (this.clients || [])
        .filter((c) => !this.ratings.some((r) => r.clientEmail === c.email))
        .map((client) => ({
          label: `${client.fullName} (${client.email})`,
          value: client.email,
        }));
    },
  },

  methods: {
    paginationHandler(page) {
      this.currentPage = page;
    },

    handleSortChange({ prop, order }) {
      this.sort = { prop, order };
      this.currentPage = 1;
    },

    async fetchClientsRatings() {
      try {
        this.loading = true;

        const { data } = await this.$api.get(`/users/${this.coach.id}/ratings`);

        this.ratings = data;
      } catch (e) {
        this.$errorToast("Impossible de récupérer les avis des clients");
      } finally {
        this.loading = false;
      }
    },

    editRating(rating) {
      this.ratingModal.rating = {
        id: rating.id,
        clientEmail: rating.clientEmail,
        rating: rating.rating,
        comment: rating.comment,
      };
      this.ratingModal.show = true;
    },

    closeEditModal() {
      this.ratingModal.show = false;
      this.ratingModal.loading = false;
      this.ratingModal.error = null;
      this.ratingModal.rating = {
        id: null,
        clientEmail: "",
        rating: null,
        comment: "",
      };
    },

    checkModalFields() {
      const { clientEmail, rating } = this.ratingModal.rating;

      if (!clientEmail) {
        this.ratingModal.error = "Veuillez sélectionner un client.";

        return false;
      }

      if (!rating || rating < 1 || rating > 5) {
        this.ratingModal.error = "Veuillez sélectionner une note entre 1 et 5.";

        return false;
      }

      this.ratingModal.error = null;

      return true;
    },

    async handleAddRating() {
      const isValid = this.checkModalFields();

      if (!isValid) {
        return;
      }

      try {
        this.ratingModal.loading = true;

        const { clientEmail, rating, comment } = this.ratingModal.rating;

        const { data: newRating } = await this.$api.post(
          `/users/${this.coach.id}/ratings`,
          {
            clientEmail,
            rating,
            comment,
          },
        );

        this.ratings.unshift(newRating);

        this.closeEditModal();
        this.$successToast("Avis ajouté avec succès");
      } catch (e) {
        this.$errorToast("Impossible d'ajouter l'avis");
      } finally {
        this.ratingModal.loading = false;
      }
    },

    async handleEditRating() {
      const isValid = this.checkModalFields();

      if (!isValid) {
        return;
      }

      try {
        this.ratingModal.loading = true;

        const { id, clientEmail, rating, comment } = this.ratingModal.rating;

        const { data: updatedRating } = await this.$api.put(
          `/users/${this.coach.id}/ratings/${id}`,
          {
            clientEmail,
            rating,
            comment,
          },
        );

        this.ratings = this.ratings.map((r) =>
          r.id === updatedRating.id ? { ...r, ...updatedRating } : r,
        );

        this.closeEditModal();
        this.$successToast("Avis mis à jour avec succès");
      } catch (e) {
        this.$errorToast("Impossible de mettre à jour l'avis");
      } finally {
        this.ratingModal.loading = false;
      }
    },
  },
};
</script>
